import { AlertColor, CircularProgress, Stack, Typography } from '@mui/material'
import { get } from 'lodash'
import React, { createContext, useContext, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Id, toast } from 'react-toastify'
import { logErrorWithContext } from 'src/utils/errorLogger'

interface ErrorOptions {
  silent?: boolean
  context?: { [key: string]: any }
}

interface SnackContextInterface {
  snack: SnackTypes
  downloadSnack: { status: DownloadSnackStatus; message?: string }
  uploadSnack: { status: UploadSnackStatus; message?: string }
  setSnack: React.Dispatch<React.SetStateAction<SnackTypes>>
  openSuccess: (text: string) => void
  openError: (error: string | Error | any, options?: ErrorOptions | { [key: string]: any }) => void
  openInfo: (text: string) => void
  setDownloadSnack(params: { status: 'complete'; message?: string; autoHideDuration?: number }): void
  setDownloadSnack(params: { status: 'start' | 'close'; message?: string }): void
  setUploadSnack(params: { status: 'complete'; message?: string; autoHideDuration?: number }): void
  setUploadSnack(params: { status: 'start' | 'close'; message?: string }): void
  copyToClipboard(value: string): void
}

export type SnackTypes = {
  open: boolean
  type: AlertColor
  text: string
  autoHideDuration?: number
  description?: string
}

type DownloadSnackStatus = 'start' | 'complete' | 'close'
type UploadSnackStatus = 'start' | 'complete' | 'close'

const initialState = {} as SnackContextInterface

const SnackContext = createContext<SnackContextInterface>(initialState)

export const useSnackData = () => useContext(SnackContext)

const SnackContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [params] = useSearchParams()

  const snack: SnackTypes = {
    open: false,
    type: 'success',
    text: '',
    description: ''
  }

  const setSnack = ({ type, text, autoHideDuration }: SnackTypes) => {
    // Note: Canceled requests are filtered in ErrorContext.handleError
    toast[type](text, {
      toastId: text,
      autoClose: autoHideDuration ?? 5000
    })
  }

  const downloadStatusRef = React.useRef<Id>()
  const downloadSnack: SnackContextInterface['downloadSnack'] = {
    status: 'close'
  }
  const setDownloadSnackStatus = ({ status, message }: SnackContextInterface['downloadSnack']) => {
    if (status === 'start') {
      if (downloadStatusRef?.current) {
        toast.dismiss(downloadStatusRef.current)
      }
      downloadStatusRef.current = toast.info(
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}>
          <CircularProgress
            size={24}
            sx={{ color: '#FFFFFF' }}
          />
          <Typography
            color="#FFFFFF"
            fontSize={16}>
            {message || 'Downloading'}
          </Typography>
        </Stack>,
        {
          autoClose: 30000,
          closeButton: false,
          hideProgressBar: true,
          icon: false
        }
      )
    } else if (status === 'complete') {
      if (downloadStatusRef?.current)
        toast.update(downloadStatusRef?.current, {
          render: message || 'Download Completed',
          type: 'success',
          isLoading: false,
          autoClose: 10000,
          closeButton: true,
          closeOnClick: false
        })
    } else {
      // status === 'close'
      if (downloadStatusRef?.current) toast.dismiss(downloadStatusRef.current)
    }
  }

  const uploadStatusRef = React.useRef<Id>()
  const uploadSnack: SnackContextInterface['uploadSnack'] = {
    status: 'close'
  }
  const setUploadSnackStatus = ({ status, message }: SnackContextInterface['uploadSnack']) => {
    if (status === 'start')
      uploadStatusRef.current = downloadStatusRef.current = toast.info(
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}>
          <CircularProgress
            size={24}
            sx={{ color: '#FFFFFF' }}
          />
          <Typography
            color="#FFFFFF"
            fontSize={16}>
            {message || 'Uploading'}
          </Typography>
        </Stack>,
        {
          autoClose: 10000,
          closeButton: false,
          hideProgressBar: true,
          icon: false
        }
      )
    else if (status === 'complete') {
      if (uploadStatusRef?.current)
        toast.update(uploadStatusRef?.current, {
          render: message || 'Upload Completed',
          type: 'success',
          isLoading: false,
          autoClose: 5000,
          closeButton: true,
          closeOnClick: false
        })
    } else {
      // status === 'close'
      if (uploadStatusRef?.current) toast.dismiss(uploadStatusRef.current)
    }
  }

  const openSuccess = (text: string, autoHideDuration?: number) => setSnack({ type: 'success', open: true, text, autoHideDuration })

  // Enhanced openError with automatic error logging and silent option
  // Note: Canceled requests are filtered in ErrorContext.handleError
  const openError = (error: string | Error | any, options?: ErrorOptions | { [key: string]: any }) => {
    // Normalize options - support both old { context } and new { silent, context } formats
    // Default: silent=true (no snackbar), pass { silent: false } to show snackbar
    const isSilent = options && 'silent' in options ? options.silent : true
    const context = options && 'context' in options ? options.context : undefined

    // Extract error message for display
    let errorMessage: string
    if (typeof error === 'string') {
      errorMessage = error
    } else if (error instanceof Error) {
      errorMessage = error.message
    } else {
      errorMessage = get(error, 'message', 'Something went wrong')
    }

    // Show error to user (skip if silent mode)
    if (!isSilent && error && (typeof error === 'string' || typeof error === 'object')) {
      if (typeof error === 'object') {
        setSnack({ type: 'error', open: true, text: get(error, 'message', error) })
      } else {
        setSnack({ type: 'error', open: true, text: error })
      }
    }

    // Log the error with context for debugging (always log)
    logErrorWithContext(errorMessage, {
      context,
      source: 'openError',
      component: 'SnackContext'
    })
  }

  const openInfo = (text: string, autoHideDuration?: number) => setSnack({ type: 'info', open: true, text, autoHideDuration })
  function copyToClipboard(value: string) {
    params.append('section', value)
    const newParams = params.toString()
    const { origin, pathname } = window.location
    value = `${origin}${pathname}?${newParams}`
    navigator.clipboard.writeText(value)
    openSuccess('Text copied to clipboard')
  }

  function setDownloadSnack(params: { status: 'start' | 'close'; message?: string }): void
  function setDownloadSnack(params: { status: 'complete'; message?: string; autoHideDuration?: number }): void
  function setDownloadSnack(params: { status: 'start' | 'close' | 'complete'; message?: string; autoHideDuration?: number }) {
    const { status, autoHideDuration, message } = params
    setDownloadSnackStatus({ status, message })
    if (status === 'complete') {
      setTimeout(() => {
        setDownloadSnackStatus({ status: 'close', message })
      }, autoHideDuration || 3000)
    }
  }

  function setUploadSnack(params: { status: 'start' | 'close'; message?: string }): void
  function setUploadSnack(params: { status: 'complete'; message?: string; autoHideDuration?: number }): void
  function setUploadSnack(params: { status: 'start' | 'close' | 'complete'; message?: string; autoHideDuration?: number }) {
    const { status, autoHideDuration, message } = params
    setUploadSnackStatus({ status, message })
    if (status === 'complete') {
      setTimeout(() => {
        setUploadSnackStatus({ status: 'close', message })
      }, autoHideDuration || 3000)
    }
  }

  const contextValue = useMemo(
    () => ({
      snack,
      downloadSnack,
      uploadSnack,
      setSnack,
      openSuccess,
      openError,
      openInfo,
      copyToClipboard,
      setDownloadSnack,
      setUploadSnack
    }),
    [snack, downloadSnack, uploadSnack, setSnack, openSuccess, openError, openInfo, copyToClipboard, setDownloadSnack, setUploadSnack]
  )

  return (
    <>
      <SnackContext.Provider value={contextValue}>{children}</SnackContext.Provider>
    </>
  )
}

export default SnackContextProvider
